import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useBrand } from "../lib/brand";
import { useAuth } from "../lib/auth";
import { THEMES } from "../lib/theme";
import { Switch } from "../components/ui/switch";
import { PageHeader, Panel, NeonButton } from "../components/common";

const SWATCHES = ["#CCFF00", "#37E5D7", "#7000FF", "#FFB800", "#FF3D9A", "#FFFFFF"];
const inp = "bg-field border border-fieldline rounded-sm px-3 py-2 text-sm w-full focus:outline-none focus:border-brand";
const WEBHOOK = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/stripe`;

export default function Settings() {
  const { brand, setBrand } = useBrand();
  const { isOwner } = useAuth();
  const [form, setForm] = useState(null);
  useEffect(() => { if (brand && !form) setForm(brand); }, [brand, form]);
  if (!form) return null;

  const save = async () => {
    const { data } = await api.put("/settings", { brand_name: form.brand_name, merchant_name: form.merchant_name, support_email: form.support_email, accent_color: form.accent_color, dry_run: form.dry_run ?? true, default_theme: form.default_theme || "aegis", monthly_target: form.monthly_target ?? 5000, rev_share_pct: form.rev_share_pct ?? 0.15 });
    setBrand(data);
    toast.success("White-label settings applied");
  };

  return (
    <div data-testid="settings-page">
      <PageHeader eyebrow="// white-label" title="Brand & Integrations" />
      <div className="grid grid-cols-12 gap-6">
        <Panel title="Brand identity" className="col-span-12 lg:col-span-6">
          <div className="space-y-4">
            {[["brand_name", "Platform name"], ["merchant_name", "Merchant legal name"], ["support_email", "Dispute contact email"]].map(([k, l]) => (
              <div key={k}><p className="label-xs mb-1">{l}</p><input className={inp} disabled={!isOwner} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`settings-${k}`} /></div>
            ))}
            <div><p className="label-xs mb-2">Accent color <span className="normal-case tracking-normal text-white/35">· applies to the Aegis Lime theme</span></p>
              <div className="flex gap-3">
                {SWATCHES.map((c) => (
                  <button key={c} onClick={() => setForm({ ...form, accent_color: c })} data-testid={`swatch-${c.slice(1)}`}
                    className={`w-9 h-9 rounded-sm border-2 transition-transform hover:scale-110 ${form.accent_color === c ? "border-white" : "border-transparent"}`} style={{ background: c }} />
                ))}
              </div>
            </div>
            <div className="mb-4" data-testid="workspace-theme-default">
              <p className="label-xs mb-1">Workspace default theme · each user can still pick their own</p>
              <select disabled={!isOwner} value={form.default_theme || "aegis"} onChange={(e) => setForm({ ...form, default_theme: e.target.value })} className={inp} data-testid="settings-default-theme">
                {THEMES.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <label className="flex items-center justify-between border border-white/10 px-3 py-3 text-sm" data-testid="dry-run-row">
              <span><b className="font-mono text-xs">DRY-RUN MODE</b><span className="block text-xs text-white/45">Run the full workflow (submit, appeal, refund) without anything leaving the platform</span></span>
              <Switch checked={form.dry_run ?? true} onCheckedChange={(v) => setForm({ ...form, dry_run: v })} data-testid="dry-run-switch" />
            </label>
            {isOwner ? <NeonButton onClick={save} data-testid="save-settings-btn">Apply settings</NeonButton> : <span className="font-mono text-[10px] text-white/40" data-testid="settings-owner-only">Only the owner can change settings</span>}
          </div>
        </Panel>
        <Panel title="Stripe webhook listener" className="col-span-12 lg:col-span-6">
          <p className="text-sm text-[#AAA]">Point your Stripe dashboard webhook to this endpoint and subscribe to <code className="text-brand">charge.dispute.*</code> events. Set <code>STRIPE_WEBHOOK_SECRET</code> in the backend env to enforce signature verification.</p>
          <pre className="mt-4 p-4 bg-canvas border border-line rounded-sm text-[11px] text-brand break-all whitespace-pre-wrap" data-testid="webhook-url">{WEBHOOK}</pre>
        </Panel>
      </div>
    </div>
  );
}
